import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Search,
  BookOpen,
  Play,
  Globe,
  Leaf,
  Clock,
  Star,
  ChevronRight,
  TrendingUp,
  Award,
  Brain,
  Target,
  CheckCircle,
  ArrowRight,
  User,
  Heart,
  Zap,
  TreePine,
  Waves,
  Sun,
  Wind,
  Mountain,
  Home,
} from "lucide-react";
import { useSupabaseAuth } from "../context/SupabaseAuthContext";
import { useToast } from "../hooks/use-toast";
import { ThemeToggle } from "../components/ThemeToggle";
import UserMenu from "../components/auth/UserMenu";
import SEO from "../components/SEO";

// Simplified learning paths data focused on key environmental topics
const learningPaths = [
  {
    id: 1,
    title: "Climate Change Fundamentals",
    description:
      "Understand the science behind climate change and its global impacts",
    icon: <Globe className="h-6 w-6" />,
    duration: "4 weeks",
    difficulty: "Beginner",
    progress: 0,
    modules: 8,
    color: "from-blue-500 to-cyan-500",
    category: "Climate Science",
  },
  {
    id: 2,
    title: "Biodiversity Conservation",
    description: "Learn about protecting ecosystems and endangered species",
    icon: <TreePine className="h-6 w-6" />,
    duration: "3 weeks",
    difficulty: "Intermediate",
    progress: 25,
    modules: 6,
    color: "from-green-500 to-emerald-500",
    category: "Conservation",
  },
  {
    id: 3,
    title: "Renewable Energy Systems",
    description: "Explore solar, wind, and other sustainable energy solutions",
    icon: <Sun className="h-6 w-6" />,
    duration: "5 weeks",
    difficulty: "Advanced",
    progress: 60,
    modules: 10,
    color: "from-yellow-500 to-orange-500",
    category: "Energy",
  },
  {
    id: 4,
    title: "Ocean Health & Marine Life",
    description: "Discover the importance of healthy oceans for our planet",
    icon: <Waves className="h-6 w-6" />,
    duration: "3 weeks",
    difficulty: "Beginner",
    progress: 0,
    modules: 7,
    color: "from-blue-600 to-teal-500",
    category: "Marine",
  },
  {
    id: 5,
    title: "Sustainable Agriculture",
    description: "Modern farming practices that protect the environment",
    icon: <Leaf className="h-6 w-6" />,
    duration: "4 weeks",
    difficulty: "Intermediate",
    progress: 40,
    modules: 9,
    color: "from-green-600 to-lime-500",
    category: "Agriculture",
  },
  {
    id: 6,
    title: "Carbon Footprint Reduction",
    description: "Practical steps to reduce your environmental impact",
    icon: <Wind className="h-6 w-6" />,
    duration: "2 weeks",
    difficulty: "Beginner",
    progress: 80,
    modules: 5,
    color: "from-gray-500 to-slate-600",
    category: "Personal Action",
  },
];

// Featured content for quick access
const featuredContent = [
  {
    id: 1,
    title: "Community Climate Wisdom",
    type: "video",
    duration: "12 min",
    thumbnail: "/api/placeholder/300/200",
    description: "Traditional ecological wisdom for modern conservation",
  },
  {
    id: 2,
    title: "Coral Reef Restoration",
    type: "article",
    readTime: "8 min",
    thumbnail: "/api/placeholder/300/200",
    description: "Scientists develop new methods to save dying reefs",
  },
  {
    id: 3,
    title: "Urban Forest Benefits",
    type: "podcast",
    duration: "25 min",
    thumbnail: "/api/placeholder/300/200",
    description: "How city trees improve air quality and mental health",
  },
];

// Quick stats for motivation
const learningStats = {
  totalLearners: "50,000+",
  completedPaths: "180,000+",
  hoursLearned: "2.5M+",
  satisfaction: "98%",
};

const KnowledgeHub = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const { user } = useSupabaseAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Get user's name for personalization
  const userName =
    user?.user_metadata?.full_name?.split(" ")[0] ||
    user?.email?.split("@")[0] ||
    "Explorer";

  // Filter learning paths based on search and category
  const filteredPaths = learningPaths.filter((path) => {
    const matchesSearch =
      path.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      path.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" || path.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Get unique categories
  const categories = [
    "all",
    ...Array.from(new Set(learningPaths.map((path) => path.category))),
  ];

  const handleStartPath = (pathId: number) => {
    const path = learningPaths.find((p) => p.id === pathId);
    if (path) {
      toast({
        title: `Starting ${path.title}`,
        description: `Welcome to your ${path.duration} learning journey!`,
      });
      // Here you would navigate to the actual learning path
      // navigate(`/learning-path/${pathId}`);
    }
  };

  const handleContinuePath = (pathId: number) => {
    const path = learningPaths.find((p) => p.id === pathId);
    if (path) {
      toast({
        title: `Continuing ${path.title}`,
        description: `You're ${path.progress}% complete. Keep going!`,
      });
      // Here you would navigate to the current module
      // navigate(`/learning-path/${pathId}/continue`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-background/90">
      <SEO
        title="Knowledge Hub - Environmental Learning Platform"
        description="Learn about climate change, conservation, and sustainability through expert-curated courses and Afro-ecological wisdom."
        keywords={[
          "environmental education",
          "climate change learning",
          "conservation courses",
          "sustainability training",
        ]}
      />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b">
        <div className="container mx-auto py-1">
          <div className="flex items-center justify-between">
            {/* Logo and Navigation */}
            <div className="flex items-center gap-6">
              {/* Beta and Version Badge */}
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-medium text-amber-500 bg-amber-500/10 px-2 py-0.5 rounded-full border border-amber-500/20">
                  Beta
                </span>
                <span className="text-[11px] text-muted-foreground">v1.1</span>
              </div>
              <Button
                variant="ghost"
                onClick={() => navigate("/")}
                className="flex items-center gap-2"
              >
                <Home className="h-4 w-4" />
                Home
              </Button>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <ThemeToggle />
              <UserMenu />
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-r from-[#40e0d0]/10 via-emerald-50/50 to-cyan-50/30 dark:from-[#40e0d0]/5 dark:via-emerald-900/10 dark:to-cyan-900/10">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-6">
              <h1 className="text-4xl md:text-6xl font-bold mb-4">
                Welcome back, <span className="text-[#40e0d0]">{userName}</span>
                !
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Discover environmental science through expert-curated learning
                paths and Afro-ecological wisdom
              </p>
            </div>

            {/* Search Bar */}
            <div className="relative max-w-2xl mx-auto mb-8">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                placeholder="Search courses, topics, or community-rooted knowledge..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 py-4 text-lg border-2 focus:border-[#40e0d0] rounded-xl shadow-lg"
              />
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
              {Object.entries(learningStats).map(([key, value]) => (
                <div key={key} className="text-center">
                  <div className="text-2xl font-bold text-[#40e0d0]">
                    {value}
                  </div>
                  <div className="text-sm text-muted-foreground capitalize">
                    {key.replace(/([A-Z])/g, " $1").trim()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-8">
        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8 justify-center">
          {categories.map((category) => (
            <Button
              key={category}
              variant={selectedCategory === category ? "default" : "outline"}
              onClick={() => setSelectedCategory(category)}
              className={`${
                selectedCategory === category
                  ? "bg-[#40e0d0] text-white"
                  : "hover:border-[#40e0d0]"
              }`}
            >
              {category === "all" ? "All Topics" : category}
            </Button>
          ))}
        </div>

        {/* Featured Content Section */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
            <Star className="h-8 w-8 text-[#40e0d0]" />
            Featured This Week
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredContent.map((content) => (
              <Card
                key={content.id}
                className="group hover:shadow-xl transition-all duration-300 cursor-pointer border-2 hover:border-[#40e0d0]/20"
              >
                <CardHeader className="p-0">
                  <div className="relative overflow-hidden rounded-t-lg">
                    <div className="w-full h-48 bg-gradient-to-br from-[#40e0d0]/20 to-emerald-200/30 dark:from-[#40e0d0]/10 dark:to-emerald-900/20" />
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" />
                    <div className="absolute top-4 right-4">
                      {content.type === "video" && (
                        <Play className="h-8 w-8 text-white" />
                      )}
                      {content.type === "article" && (
                        <BookOpen className="h-8 w-8 text-white" />
                      )}
                      {content.type === "podcast" && (
                        <Brain className="h-8 w-8 text-white" />
                      )}
                    </div>
                    <div className="absolute bottom-4 left-4">
                      <Badge variant="secondary" className="bg-white/90">
                        {content.type === "video" && content.duration}
                        {content.type === "article" && content.readTime}
                        {content.type === "podcast" && content.duration}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-2 group-hover:text-[#40e0d0] transition-colors">
                    {content.title}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {content.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Learning Paths Section */}
        <section>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-bold flex items-center gap-3">
              <Target className="h-8 w-8 text-[#40e0d0]" />
              Learning Paths
            </h2>
            <p className="text-muted-foreground">
              {filteredPaths.length} path{filteredPaths.length !== 1 ? "s" : ""}{" "}
              found
            </p>
          </div>

          {/* Learning Paths Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPaths.map((path) => (
              <Card
                key={path.id}
                className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-[#40e0d0]/20 overflow-hidden"
              >
                {/* Header with gradient and icon */}
                <CardHeader
                  className={`bg-gradient-to-r ${path.color} text-white p-6 relative`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {path.icon}
                      <Badge
                        variant="secondary"
                        className="bg-white/20 text-white border-0"
                      >
                        {path.difficulty}
                      </Badge>
                    </div>
                    {path.progress > 0 && (
                      <div className="text-right">
                        <div className="text-sm opacity-90">
                          {path.progress}%
                        </div>
                        <div className="w-16 h-2 bg-white/30 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-white transition-all duration-500"
                            style={{ width: `${path.progress}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="p-6">
                  <div className="mb-4">
                    <h3 className="font-bold text-lg mb-2 group-hover:text-[#40e0d0] transition-colors">
                      {path.title}
                    </h3>
                    <p className="text-muted-foreground text-sm mb-4">
                      {path.description}
                    </p>
                  </div>

                  {/* Path metadata */}
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-6">
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {path.duration}
                    </div>
                    <div className="flex items-center gap-1">
                      <BookOpen className="h-4 w-4" />
                      {path.modules} modules
                    </div>
                  </div>

                  {/* Action button */}
                  <div className="space-y-3">
                    {path.progress > 0 ? (
                      <>
                        <Button
                          className="w-full bg-[#40e0d0] hover:bg-[#40e0d0]/90 text-white"
                          onClick={() => handleContinuePath(path.id)}
                        >
                          Continue Learning
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          {Math.round(
                            (path.progress / 100) * path.modules,
                          )} of {path.modules} modules completed
                        </div>
                      </>
                    ) : (
                      <Button
                        variant="outline"
                        className="w-full border-[#40e0d0] text-[#40e0d0] hover:bg-[#40e0d0] hover:text-white"
                        onClick={() => handleStartPath(path.id)}
                      >
                        Start Learning
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Empty state */}
          {filteredPaths.length === 0 && (
            <div className="text-center py-12">
              <Search className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-xl font-semibold mb-2">
                No learning paths found
              </h3>
              <p className="text-muted-foreground mb-4">
                Try adjusting your search terms or selected category
              </p>
              <Button
                onClick={() => {
                  setSearchQuery("");
                  setSelectedCategory("all");
                }}
              >
                Clear Filters
              </Button>
            </div>
          )}
        </section>

        {/* Call-to-Action */}
        <section className="mt-16 text-center bg-gradient-to-r from-[#40e0d0]/10 to-emerald-50/50 dark:from-[#40e0d0]/5 dark:to-emerald-900/10 rounded-2xl p-12">
          <Award className="h-16 w-16 mx-auto text-[#40e0d0] mb-4" />
          <h2 className="text-3xl font-bold mb-4">Ready to Make an Impact?</h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join thousands of learners who are building the knowledge needed to
            protect our planet
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-[#40e0d0] hover:bg-[#40e0d0]/90 text-white"
              onClick={() => {
                const firstIncomplete = learningPaths.find(
                  (path) => path.progress === 0,
                );
                if (firstIncomplete) handleStartPath(firstIncomplete.id);
              }}
            >
              Start Your First Course
              <Zap className="ml-2 h-5 w-5" />
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate("/")}
              className="border-[#40e0d0] text-[#40e0d0] hover:bg-[#40e0d0] hover:text-white"
            >
              Explore Environmental Data
              <Globe className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default KnowledgeHub;
